<template>
	<BaseModal @modal-close="close">
		<div class="media-gallery coterminous" @click="close">
			<div v-if="loading" class="loader alpha coterminous" style="margin: auto"></div>
			<div class="media-gallery__item coterminous fade in">
				<img
					v-if="item.mediaItemType === 'PHOTO'"
					:src="item.sourceUrl"
					alt="Gallery photo"
					class="media-gallery__img"
					@click.stop
				/>

				<div
					v-if="item.mediaItemType === 'VIDEO' && props.isYoutubeVideo"
					class="media-gallery__video-wrapper position-relative img-lazy-wrapper"
					@click.stop
				>
					<iframe
						id="ytplayer"
						class="border-none"
						type="text/html"
						width="640"
						height="360"
						:src="`https://www.youtube.com/embed/${getIdFromURL(
							item.sourceUrl
						)}?rel=0&modestbranding=1&iv_load_policy=3`"
						title="Video Player"
					></iframe>
				</div>
				<div
					v-else-if="item.mediaItemType === 'VIDEO'"
					class="media-gallery__video-wrapper position-relative img-lazy-wrapper"
					@click.stop
				>
					<video
						id="prismicVideoContainer"
						controls
						allow="fullscreen"
						crossorigin="Anonymous"
						height="100%"
						width="100%"
						playsinline="true"
						class="prismic-video-container"
						><source :src="item.sourceUrl" type="video/mp4" />
						<track default kind="captions" srclang="en" src="" />
					</video>
				</div>

				<div v-if="item.caption" class="media-gallery__item-caption zeta-smo reversed text-align-center mt-50">
					{{ item.caption }}
				</div>
				<div
					class="media-gallery__page-count text-color-white text-align-center delta mt-50 line-height-125-md"
				>
					{{ selectedIndex + 1 }} of {{ props.mediaItems.length }}
				</div>
			</div>
			<div v-if="props.mediaItems.length > 1" @click.stop>
				<BaseButtonIconOnly
					class="media-gallery__prev"
					:elevation="true"
					icon-dir="left"
					icon="chevron"
					@click.stop="previous()"
				/>
				<BaseButtonIconOnly
					class="media-gallery__next"
					:elevation="true"
					icon-dir="right"
					icon="chevron"
					@click.stop="next()"
				/>
			</div>
			<BaseButtonIconOnly class="media-gallery__close" :unstyled="true" icon="close" @click="close" />
		</div>
	</BaseModal>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { InvestorUpdateMediaItem } from 'types/account';

interface Props {
	mediaItems: Array<InvestorUpdateMediaItem>;
	selected?: number;
	investorUpdateId?: string;
	isYoutubeVideo?: boolean;
}

interface Emits {
	(e: 'modal-close'): void;
}

const props = withDefaults(defineProps<Props>(), {
	selected: 0,
	investorUpdateId: '',
	isYoutubeVideo: true
});

const emits = defineEmits<Emits>();

const selectedIndex = ref(0);

const loading = ref(false);

const item = computed((): InvestorUpdateMediaItem => props.mediaItems[selectedIndex.value]);

function getIdFromURL(youtubeUrl: string): string {
	const youtubeRegexp =
		/https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/gi;
	let id = youtubeUrl.replace(youtubeRegexp, '$1');

	if (id.includes(';')) {
		const pieces = id.split(';');

		if (pieces[1].includes('%')) {
			const uriComponent = decodeURIComponent(pieces[1]);
			id = `http://youtube.com${uriComponent}`.replace(youtubeRegexp, '$1');
		} else {
			id = pieces[0];
		}
	} else if (id.includes('#')) {
		id = id.split('#')[0];
	}

	return id;
}

function previous(): void {
	selectedIndex.value--;
	selectedIndex.value = selectedIndex.value < 0 ? props.mediaItems.length - 1 : selectedIndex.value;
}

function next(): void {
	selectedIndex.value++;
	selectedIndex.value = selectedIndex.value > props.mediaItems.length - 1 ? 0 : selectedIndex.value;
}

function close(): void {
	emits('modal-close');
}

function handleKeyDown(e: KeyboardEvent): void {
	switch (e.key) {
		case 'ArrowLeft':
			previous();
			break;
		case 'ArrowRight':
			next();
			break;
		case 'Escape':
			close();
			break;
		default:
			break;
	}
}

function setup(): void {
	selectedIndex.value = props.selected;
	document.addEventListener('keydown', handleKeyDown);
}

onUnmounted(() => {
	document.removeEventListener('keydown', handleKeyDown);
});

setup();
</script>

<style lang="scss" scoped>
.prismic-video-container {
	object-fit: cover;
}
</style>
